import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BUILD_NUMBER, BUILD_SUBVERSION } from '@core/constants';
import { Sidenav, SidenavEmployeeInfo, SidenavMenuLink } from '@core/models';
import { appRoutePaths } from '@core/routes/route-paths.constants';
import { AuthService, AvatarService, SidenavService, SysTextService, WidgetService } from '@core/services';
import { ComingSoonComponent } from '@modules/dialogs/coming-soon/coming-soon.component';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidenavComponent implements OnInit {
  static readonly componentName = 'SidenavComponent';
  
  @Output() linkClick = new EventEmitter<void>();
  @Output() onLogout = new EventEmitter<void>();

  public data$: Observable<Sidenav>;

  get employeeInfo$(): Observable<SidenavEmployeeInfo> {
    return this.avatarService.avatar$;
  }

  private readonly templateNames = {
    comingSoon: 'comingSoonDialog',
    setupDashboard: 'setupDashboard',
    emailTemplatesWidget: 'emailTemplatesWidget',
    actionItemsTemplatesWidget: 'actionItemsTemplatesWidget',
    workSchedulesWidget: 'workSchedulesWidget',
    leaveReasonsWidget: 'leaveReasonsWidget'
  };

  private comingSoonDialogData: any;
  private comingSoonDialogRef: MatDialogRef<ComingSoonComponent>;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private sidenavService: SidenavService,
    private avatarService: AvatarService,
    private authService: AuthService,
    private dialog: MatDialog,
    private textService: SysTextService,
    private widgetService: WidgetService
  ) {}

  ngOnInit() {
    this.data$ = this.loadData();

    this.avatarService.sidenavUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.data$ = null;
        this.data$ = this.loadData();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.onLogout.emit();
    this.linkClick.emit();
    this.authService.logout(true);
  }

  handleLinkClick(link: SidenavMenuLink): void {
    const route = link ? link.route : null;
    this.linkClick.emit();
  }

  private loadData(): Observable<Sidenav> {
    const data$ = this.sidenavService.getSidenavInfo();
    const sysText$ = this.textService.getSysText(this.templateNames).pipe(
      tap((res) => {
        this.comingSoonDialogData = res[this.templateNames.comingSoon];
      })
    );

    return combineLatest([data$, sysText$]).pipe(
      switchMap(([data, sysText]) => this.addSetupPages(data.links, sysText).pipe(
        map((links) => {
          return { ...data, links, copyright: `${data.copyright}.${BUILD_SUBVERSION}.${BUILD_NUMBER}` };
        })
      ))
    );
  }

  // Setup's pages that the user's role has (the Setup page's cards) nest under its item: a second way to them (WCAG 2.4.5)
  private addSetupPages(links: SidenavMenuLink[], sysText: any): Observable<SidenavMenuLink[]> {
    const setupLink = links.find((link) => link.route?.replace(/^\//, '').split('/')[0] === appRoutePaths.SETUP);
    if (!setupLink) {
      return of(links);
    }

    return this.widgetService.getSetupWidgetInfo().pipe(
      map((info) => {
        const pages = [
          {
            available: info.et?.available,
            title: sysText[this.templateNames.emailTemplatesWidget].emailTemplatesWidget_title,
            route: appRoutePaths.EMAIL_TEMPLATES
          },
          {
            available: info.ait?.available,
            title: sysText[this.templateNames.actionItemsTemplatesWidget].actionItemsTemplatesWidget_title,
            route: appRoutePaths.ACTIONITEM_TEMPLATES
          },
          {
            available: info.ws?.available,
            title: sysText[this.templateNames.workSchedulesWidget].workSchedulesWidget_title,
            route: appRoutePaths.WORK_SCHEDULES
          },
          {
            available: info.lr?.available,
            title: sysText[this.templateNames.leaveReasonsWidget].leaveReasonsWidget_title,
            route: appRoutePaths.LEAVE_REASONS
          }
        ]
          .filter((page) => page.available)
          .map((page) => ({ title: page.title, route: '/' + page.route }));

        if (!pages.length) {
          return links;
        }

        const childrenToggleAriaLabel = sysText[this.templateNames.setupDashboard].setupPages_ariaLabel;
        return links.map((link) => link === setupLink ? { ...link, children: pages, childrenToggleAriaLabel } : link);
      })
    );
  }

  openComingSoonDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: ComingSoonComponent.componentName,
      width: '280px',
      disableClose: false,
      data: this.comingSoonDialogData
    };

    this.comingSoonDialogRef = this.dialog.open(ComingSoonComponent, dialogConfig);
  }
}
